// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useState } from 'react';

import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Slider from '@cloudscape-design/components/slider';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Toggle from '@cloudscape-design/components/toggle';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';

import { InfoLink } from '../../commons/common-components';
import { DetailsState, ToolsContent, WizardState } from '../interfaces';
import { TOOLS_CONTENT, NODEPOOL_OPTIONS, CLASS_OPTIONS, AVAILABILITY_ZONES, CHECKPOINT_MODE_OPTIONS } from '../steps-config';
import { toOption } from '../types';

const { details: detailsToolsContent } = TOOLS_CONTENT;

interface InstanceOptionsProps extends DetailsState {
  setHelpPanelContent: (tools: ToolsContent) => void;
  onChange: (state: Partial<DetailsState>) => void;
}

interface JobManager extends DetailsState {
  setHelpPanelContent: (tools: ToolsContent) => void;
  onDetailsChange: (state: Partial<DetailsState>) => void;
}

const JobManager = ({
  jobManager_availability_zone,
  // jobManager_instance_class,
  jobManager_nodepool,
  jobManager_instance_size,
  jobManager_resources_requests_memory,
  jobManager_resources_requests_cpu,
  jobManager_resources_limits_memory,
  jobManager_resources_limits_cpu,
  onChange,
  setHelpPanelContent,
}: InstanceOptionsProps) => {
  const validateStorageValue = (value: number, range: [number, number]) => {
    if (value === undefined) {
      return '';
    }
    if (value < range[0] || value > range[1]) {
      return 'Enter a valid storage amount.';
    }

    return '';
  };

  return (
    <Container header={<Header variant="h2">Job Manager</Header>}>
      <SpaceBetween size="l">
        <FormField
          label="Node (VM) instance"
          info={<InfoLink onFollow={() => setHelpPanelContent(detailsToolsContent.jmnodepool)} />}
        // description="Selects the type of node this application will use.  Choose spot or on-demand and tailor your choice for memort, cpu or general"
        // constraintText="Case sensitive, so stored as lower-case. Must contain from 1 to 63 alphanumeric characters or hyphens. First character must be a letter. Cannot end with a hyphen or contain two consecutive hyphens."
        >
          <Select
            options={NODEPOOL_OPTIONS}
            onChange={event => onChange({ jobManager_nodepool: event.detail.selectedOption })}
            selectedAriaLabel="Selected"
            selectedOption={jobManager_nodepool}
          />
        </FormField>
        <FormField
          label="Instance size"
        // info={<InfoLink onFollow={() => setHelpPanelContent(detailsToolsContent.jobManager_nodepool)} />}
        // description="A value that sets the size of the instance on which the Job Manager will run."
        // constraintText=""
        >
          <Select
            options={CLASS_OPTIONS}
            onChange={event => onChange({ jobManager_instance_size: event.detail.selectedOption })}
            selectedAriaLabel="Selected"
            selectedOption={jobManager_instance_size}
          />
        </FormField>
        <FormField
          label="Availability zone"
          info={<InfoLink onFollow={() => setHelpPanelContent(detailsToolsContent.jmnodepool)} />}
        >
          <Select
            options={AVAILABILITY_ZONES}
            onChange={event => onChange({ jobManager_availability_zone: toOption(event.detail.selectedOption) })}
            selectedAriaLabel="Selected"
            selectedOption={jobManager_availability_zone}
          />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField
            label="Memory allocated to the job manager"
            // description="Higher allocated storage may improve IOPS performance."
            errorText={validateStorageValue(Number(jobManager_resources_requests_memory), [1, 64])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(jobManager_resources_requests_memory)}
                  value={Number.isNaN(Number(jobManager_resources_requests_memory)) ? 1 : Number(jobManager_resources_requests_memory)}
                  onChange={event => onChange({ jobManager_resources_requests_memory: String(event.detail.value) })}
                  min={1}
                  max={64}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="345"
                    value={jobManager_resources_requests_memory}
                    //value={Number.isNaN(Number(jobManager_resources_requests_memory)) ? 1 : Number(jobManager_resources_requests_memory)}
                    onChange={event => onChange({ jobManager_resources_requests_memory: String(event.detail.value) })}
                  />
                </div>
                <div>GiB</div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="CPU allocated to the job manager"
            // description="More CPU may improve performance but will incur extra costs."
            errorText={validateStorageValue(Number(jobManager_resources_requests_cpu), [1, 32])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(jobManager_resources_requests_cpu)}
                  value={Number.isNaN(Number(jobManager_resources_requests_cpu)) ? 1 : Number(jobManager_resources_requests_cpu)}
                  onChange={event => onChange({ jobManager_resources_requests_cpu: String(event.detail.value) })}
                  min={1}
                  max={32}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="123"
                    value={jobManager_resources_requests_cpu}
                    onChange={event => onChange({ jobManager_resources_requests_cpu: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
        <ColumnLayout columns={2}>
          <FormField
            label="Memory limits applied to the job manager"
            // description="Limit the memory."
            errorText={validateStorageValue(Number(jobManager_resources_limits_memory), [1, 80])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(jobManager_resources_limits_memory)}
                  value={Number.isNaN(Number(jobManager_resources_limits_memory)) ? 1 : Number(jobManager_resources_limits_memory)}
                  onChange={event => onChange({ jobManager_resources_limits_memory: String(event.detail.value) })}
                  min={1}
                  max={80}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="storage"
                    value={jobManager_resources_limits_memory}
                    onChange={event => onChange({ jobManager_resources_limits_memory: String(event.detail.value) })}
                  />
                </div>
                <div>GiB</div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="CPU limits applied to the job manager"
            // description="More CPU may improve performance but will incur extra costs."
            errorText={validateStorageValue(Number(jobManager_resources_limits_cpu), [1, 64])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(jobManager_resources_limits_cpu)}
                  value={Number.isNaN(Number(jobManager_resources_limits_cpu)) ? 1 : Number(jobManager_resources_limits_cpu)}
                  onChange={event => onChange({ jobManager_resources_limits_cpu: String(event.detail.value) })}
                  min={1}
                  max={64}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="storage"
                    value={jobManager_resources_limits_cpu}
                    onChange={event => onChange({ jobManager_resources_limits_cpu: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
      </SpaceBetween>
    </Container>
  );
};

interface TaskManager extends DetailsState {
  setHelpPanelContent: (tools: ToolsContent) => void;
  onDetailsChange: (state: Partial<DetailsState>) => void;
}

const TaskManager = ({
  taskManager_nodepool,
  taskManager_instance_size,
  taskManager_availability_zone,
  taskManager_resources_requests_cpu,
  taskManager_resources_limits_cpu,
  taskManager_resources_limits_memory,
  taskManager_resources_requests_memory,
  taskManager_scale_min_replicas,
  taskManager_scale_max_Replicas,
  taskManager_scale_average_utilization,
  taskManager_scale_enabled,
  taskManager_number_of_task_slots,
  onChange,
  setHelpPanelContent,
}: InstanceOptionsProps) => {
  const validateStorageValue = (value: number, range: [number, number]) => {
    if (value === undefined) {
      return '';
    }
    if (value < range[0] || value > range[1]) {
      return `Enter a valid value greater than ${range[0]}) and less than ${range[1]}.`;
    }

    return '';
  };

  return (
    <Container header={<Header variant="h2">Task Manager</Header>}>
      <SpaceBetween size="l">
        <FormField
          label="Node (VM) instance"
          info={<InfoLink onFollow={() => setHelpPanelContent(detailsToolsContent.nodepool)} />}
        // description="Selects the type of node this application will use.  Choose spot or on-demand and tailor your choice for memort, cpu or general"
        // constraintText="Case sensitive, so stored as lower-case. Must contain from 1 to 63 alphanumeric characters or hyphens. First character must be a letter. Cannot end with a hyphen or contain two consecutive hyphens."
        >
          <Select
            options={NODEPOOL_OPTIONS}
            onChange={event => onChange({ taskManager_nodepool: toOption(event.detail.selectedOption) })}
            selectedAriaLabel="Selected"
            selectedOption={taskManager_nodepool}
          />
        </FormField>
        <FormField
          label="Instance size"
        // info={<InfoLink onFollow={() => setHelpPanelContent(detailsToolsContent.taskManager_instance_size)} />}
        // description="A value that sets the size of the instance on which the Task Manager job will run."
        // constraintText=""
        >
          <Select
            options={CLASS_OPTIONS}
            onChange={event => onChange({ taskManager_instance_size: toOption(event.detail.selectedOption) })}
            selectedAriaLabel="Selected"
            selectedOption={taskManager_instance_size}
          />
        </FormField>
        <FormField
          label="Availability zone"
          info={<InfoLink onFollow={() => setHelpPanelContent(detailsToolsContent.nodepool)} />}
        >
          <Select
            options={AVAILABILITY_ZONES}
            onChange={event => onChange({ taskManager_availability_zone: toOption(event.detail.selectedOption) })}
            selectedAriaLabel="Selected"
            selectedOption={taskManager_availability_zone}
          />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField
            label="Memory allocated to the task manager"
            // description="Higher allocated storage may improve IOPS performance."
            errorText={validateStorageValue(Number(taskManager_resources_requests_memory), [1, 64])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(taskManager_resources_requests_cpu)}
                  value={Number.isNaN(Number(taskManager_resources_requests_memory)) ? 1 : Number(taskManager_resources_requests_memory)}
                  onChange={event => onChange({ taskManager_resources_requests_memory: String(event.detail.value) })}
                  min={1}
                  max={64}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="taskManager_resources_requests_memory"
                    value={taskManager_resources_requests_memory}
                    onChange={event => onChange({ taskManager_resources_requests_memory: String(event.detail.value) })}
                  />
                </div>
                <div>GiB</div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="CPU allocated to the task manager"
            // description="More CPU to improve performance."
            errorText={validateStorageValue(Number(taskManager_resources_requests_cpu), [1, 32])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(taskManager_resources_limits_cpu)}
                  value={Number.isNaN(Number(taskManager_resources_requests_cpu)) ? 1 : Number(taskManager_resources_requests_cpu)}
                  onChange={event => onChange({ taskManager_resources_requests_cpu: String(event.detail.value) })}
                  min={1}
                  max={32}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="taskManager_resources_requests_cpu"
                    value={taskManager_resources_requests_cpu}
                    onChange={event => onChange({ taskManager_resources_requests_cpu: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
        <ColumnLayout columns={2}>
          <FormField
            label="Memory limits applied to the task manager."
            // description="Upperbound of memory made available to the task manager."
            errorText={validateStorageValue(Number(taskManager_resources_limits_memory), [1, 80])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(taskManager_resources_limits_memory)}
                  value={Number.isNaN(Number(taskManager_resources_limits_memory)) ? 1 : Number(taskManager_resources_limits_memory)}
                  onChange={event => onChange({ taskManager_resources_limits_memory: String(event.detail.value) })}
                  min={1}
                  max={80}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="taskManager_resources_limits_memory"
                    value={taskManager_resources_limits_memory}
                    onChange={event => onChange({ taskManager_resources_limits_memory: String(event.detail.value) })}
                  />
                </div>
                <div>GiB</div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="CPU limits applied to the task manager."
            // description="Upperbound of CPU made available to the task manager."
            errorText={validateStorageValue(Number(taskManager_resources_limits_cpu), [1, 64])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(taskManager_resources_limits_cpu)}
                  value={Number.isNaN(Number(taskManager_resources_limits_cpu)) ? 1 : Number(taskManager_resources_limits_cpu)}
                  onChange={event => onChange({ taskManager_resources_limits_cpu: String(event.detail.value) })}
                  min={1}
                  max={64}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="taskManager_resources_limits_cpu"
                    value={taskManager_resources_limits_cpu}
                    onChange={event => onChange({ taskManager_resources_limits_cpu: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
        <FormField
          label="Autoscaling (KEDA)"
          description="Scales the task manager pod count based on Flink backpressure. Mutually exclusive with Flink's built-in autoscaler, which is disabled automatically while this is on."
        >
          <Toggle
            checked={taskManager_scale_enabled === 'true'}
            onChange={event => onChange({ taskManager_scale_enabled: String(event.detail.checked) })}
          >
            {taskManager_scale_enabled === 'true' ? 'Enabled' : 'Disabled'}
          </Toggle>
        </FormField>
        <ColumnLayout columns={2}>
          <FormField
            label="Minimum number of replicas"
            // description="Lowest number of replicas to deal with standard load."
            errorText={validateStorageValue(Number(taskManager_scale_min_replicas), [1, 100])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(taskManager_resources_limits_memory)}
                  value={Number.isNaN(Number(taskManager_scale_min_replicas)) ? 1 : Number(taskManager_scale_min_replicas)}
                  onChange={event => onChange({ taskManager_scale_min_replicas: String(event.detail.value) })}
                  min={1}
                  max={100}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="taskManager_scale_min_replicas"
                    value={taskManager_scale_min_replicas}
                    onChange={event => onChange({ taskManager_scale_min_replicas: String(event.detail.value) })}
                  />
                </div>
                <div>GiB</div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="Maximum number of replicas"
            // description="Higher number of replicas to deal with increased load."
            errorText={validateStorageValue(Number(taskManager_scale_max_Replicas), [1, 100])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(taskManager_resources_limits_cpu)}
                  value={Number.isNaN(Number(taskManager_scale_max_Replicas)) ? 1 : Number(taskManager_scale_max_Replicas)}
                  onChange={event => onChange({ taskManager_scale_max_Replicas: String(event.detail.value) })}
                  min={1}
                  max={100}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="taskManager_scale_max_Replicas"
                    value={taskManager_scale_max_Replicas}
                    onChange={event => onChange({ taskManager_scale_max_Replicas: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
        <ColumnLayout columns={2}>
          <FormField
            label="Average utilization metric"
            description={
              taskManager_scale_enabled === 'true'
                ? "Not used while KEDA autoscaling is on -- KEDA scales on Flink backpressure instead, not this percentage."
                : "Sets at which point Flink's built-in autoscaler scales."
            }
            errorText={validateStorageValue(Number(taskManager_scale_average_utilization), [1, 100])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel=""
                  // value={Number(taskManager_resources_limits_cpu)}
                  value={Number.isNaN(Number(taskManager_scale_average_utilization)) ? 1 : Number(taskManager_scale_average_utilization)}
                  onChange={event => onChange({ taskManager_scale_average_utilization: String(event.detail.value) })}
                  min={1}
                  max={100}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="taskManager_scale_max_Replicas"
                    value={taskManager_scale_average_utilization}
                    onChange={event => onChange({ taskManager_scale_average_utilization: String(event.detail.value) })}
                  />
                </div>
                <div>%</div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="Number of slots assihgned to the task manager."
            // description="Sets at which point the task manager scales."
            errorText={validateStorageValue(Number(taskManager_number_of_task_slots), [1, 100])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel=""
                  // value={Number(taskManager_resources_limits_cpu)}
                  value={Number.isNaN(Number(taskManager_number_of_task_slots)) ? 1 : Number(taskManager_number_of_task_slots)}
                  onChange={event => onChange({ taskManager_number_of_task_slots: String(event.detail.value) })}
                  min={1}
                  max={100}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="taskManager_number_of_task_slots"
                    value={taskManager_number_of_task_slots}
                    onChange={event => onChange({ taskManager_number_of_task_slots: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
      </SpaceBetween>
    </Container>
  );
};

const Checkpointing = ({
  checkpoint_enabled,
  checkpoint_s3_dir,
  savepoint_s3_dir,
  checkpoint_interval_ms,
  checkpoint_timeout_ms,
  checkpoint_mode,
  onChange,
}: InstanceOptionsProps) => {
  const enabled = checkpoint_enabled === 'true';

  return (
    <Container header={<Header variant="h2">Checkpointing</Header>}>
      <SpaceBetween size="l">
        <FormField
          label="Checkpointing"
          description="Periodically snapshots pipeline state to S3 so the job can recover from failure without reprocessing from the start."
        >
          <Toggle
            checked={enabled}
            onChange={event => onChange({ checkpoint_enabled: String(event.detail.checked) })}
          >
            {enabled ? 'Enabled' : 'Disabled'}
          </Toggle>
        </FormField>
        {enabled && (
          <>
            <FormField label="Checkpoint S3 directory">
              <Input
                placeholder="s3a://<bucket>/<app>/checkpoints"
                value={checkpoint_s3_dir}
                onChange={event => onChange({ checkpoint_s3_dir: event.detail.value })}
              />
            </FormField>
            <FormField label="Savepoint S3 directory">
              <Input
                placeholder="s3a://<bucket>/<app>/savepoints"
                value={savepoint_s3_dir}
                onChange={event => onChange({ savepoint_s3_dir: event.detail.value })}
              />
            </FormField>
            <ExpandableSection headerText="Advanced">
              <SpaceBetween size="l">
                <ColumnLayout columns={2}>
                  <FormField label="Checkpoint interval (ms)">
                    <Input
                      type="number"
                      value={checkpoint_interval_ms}
                      onChange={event => onChange({ checkpoint_interval_ms: String(event.detail.value) })}
                    />
                  </FormField>
                  <FormField label="Checkpoint timeout (ms)">
                    <Input
                      type="number"
                      value={checkpoint_timeout_ms}
                      onChange={event => onChange({ checkpoint_timeout_ms: String(event.detail.value) })}
                    />
                  </FormField>
                </ColumnLayout>
                <FormField label="Checkpointing mode">
                  <Select
                    options={CHECKPOINT_MODE_OPTIONS}
                    onChange={event => onChange({ checkpoint_mode: toOption(event.detail.selectedOption) })}
                    selectedAriaLabel="Selected"
                    selectedOption={checkpoint_mode}
                  />
                </FormField>
              </SpaceBetween>
            </ExpandableSection>
          </>
        )}
      </SpaceBetween>
    </Container>
  );
};

const StorageClass = ({
  storage_access_mode,
  storage_class_name,
  onChange,
  setHelpPanelContent,
}: InstanceOptionsProps) => {
  const validateStorageValue = (value: number, range: [number, number]) => {
    if (value === undefined) {
      return '';
    }
    if (value < range[0] || value > range[1]) {
      return `Enter a valid value greater than ${range[0]}) and less than ${range[1]}.`;
    }
    return '';
  };
  const accessModes: OptionDefinition[] = [
    { label: 'ReadWriteOnce', value: 'ReadWriteOnce' },
    { label: 'ReadWriteMany', value: 'ReadWriteMany' },
  ];

  const selectedOption = accessModes.find(t => t.value === storage_access_mode) ?? null;

  return (
    <Container header={<Header variant="h2">Storage details</Header>}>
      <SpaceBetween size="l">
        <Select
          selectedOption={selectedOption}
          onChange={({ detail }) =>
            onChange({ storage_access_mode: detail.selectedOption?.value || '' })
          }
          options={accessModes}
          placeholder="Select an access mode"
        />
        <SpaceBetween size="l">
          <FormField
            label="Storage Class name"
            info={<InfoLink onFollow={() => setHelpPanelContent(detailsToolsContent.organisationName)} />}
          // description="A string that defines the name for this data-streaming instance."
          // constraintText="Must start with a letter. Must contain 1 to 64 alphanumeric characters."
          >
            <Input placeholder="example-hostpath" value={storage_class_name} onChange={event => onChange({ storage_class_name: event.detail.value })} />
          </FormField>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};
interface StorageProps {
  info: WizardState;
  setHelpPanelContent: (tools: ToolsContent) => void;
  onChange: (state: Partial<DetailsState>) => void;
}

interface DetailsProps {
  info: WizardState;
  onChange: (state: Partial<DetailsState>) => void;
  setHelpPanelContent: (tools: ToolsContent) => void;
}

const Details = ({ info: { details }, setHelpPanelContent, onChange }: DetailsProps) => {
  const childProps = { ...details, setHelpPanelContent, onChange };
  return (
    <Box margin={{ bottom: 'l' }}>
      <SpaceBetween size="l">
        <JobManager {...childProps} />
        <TaskManager {...childProps} />
        <Checkpointing {...childProps} />
        <StorageClass {...childProps} />
      </SpaceBetween>
    </Box>
  );
};
export default Details;

